// branch(조건 분기) 노드의 **활성 슬롯 판정** 단일 소스.
//
// 활성 슬롯은 렌더(RenderTemplate)·측정(pageMeasure)·낱장 재조립(branchPagination) 세 곳이 모두
// 알아야 한다. 각자 판정하면 "측정한 슬롯"과 "그린 슬롯"이 갈려, 계획에 없는 노드를 자르거나
// 계획이 가리키는 노드가 화면에 없는 상태가 된다. 분할 경계 판정(findSoleNestedBoundary)을 렌더와
// 측정이 같은 함수로 공유하는 것과 같은 원칙이다.

import type { BranchNode } from '@safetyhub/shared';
import { evaluateCondition } from '@safetyhub/shared';
import type { RenderContext, RenderData } from './types';

/**
 * 활성 슬롯 판정 입력 — 편집 캔버스(탭 명시 선택) / 그 외(조건 평가) 분기.
 *
 * `RenderContext`에서 **파생**한다(재선언 금지). 손으로 같은 필드를 다시 적으면 구조적 호환이라
 * tsc가 드리프트를 못 잡아, 렌더는 새 축을 받는데 판정만 못 받는 상태가 조용히 생긴다.
 */
export type BranchSlotContext = Pick<RenderContext, 'editing' | 'activeBranchSlots'>;

/**
 * branch 노드의 활성 슬롯 인덱스. **-1이면 미렌더**(매칭 슬롯 없음 / 슬롯이 하나도 없음).
 *
 * editing에서 조건을 평가하지 않는 이유: 실데이터 미반영이라 조건이 전부 false로 떨어져 편집이
 * 불가능해진다 → 탭으로 명시 선택한 슬롯만 렌더한다(placeholder 함정과 동일 취지).
 * 비편집은 condition 미지정('' 포함) 슬롯을 무조건 매칭(else)으로 본다 —
 * `evaluateCondition(undefined)`가 true다.
 *
 * RenderTemplate `case 'branch'`에 있던 판정을 그대로 옮긴 것으로 동작은 불변이다.
 */
export function resolveActiveBranchSlot(
  node: BranchNode,
  data: RenderData,
  opts: BranchSlotContext = {},
): number {
  const branches = node.branches;
  if (opts.editing === true) {
    const raw = opts.activeBranchSlots?.[node.id] ?? 0;
    const clamped = raw >= 0 && raw < branches.length ? raw : 0;
    return clamped < branches.length ? clamped : -1;
  }
  const idx = branches.findIndex((slot) => evaluateCondition(slot.condition, data));
  return idx >= 0 && idx < branches.length ? idx : -1;
}
